// Import a pay stub from somewhere else: a PDF or a photo of a stub from
// ADP, Gusto, Workday, a prior employer. The text is pulled out right here
// in the browser (pdf.js for PDFs' text layer, tesseract OCR for photos —
// nothing is uploaded anywhere), then pattern-matched into the fields our
// forms understand. Pro/Enterprise.

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };

export async function extractStubText(file, onProgress) {
  if (file.type === "application/pdf" || /\.pdf$/i.test(file.name || "")) {
    onProgress?.("Reading the PDF…");
    const pdfjs = await import("pdfjs-dist");
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
    const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
    const parts = [];
    for (let i = 1; i <= Math.min(pdf.numPages, 4); i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      // keep line structure: group items by their y position
      const rows = new Map();
      for (const item of content.items) {
        const y = Math.round(item.transform[5]);
        if (!rows.has(y)) rows.set(y, []);
        rows.get(y).push({ x: item.transform[4], str: item.str });
      }
      [...rows.entries()].sort((a, b) => b[0] - a[0]).forEach(([, items]) => {
        parts.push(items.sort((a, b) => a.x - b.x).map((it) => it.str).join(" "));
      });
    }
    return parts.join("\n");
  }
  // a photo: OCR it locally
  onProgress?.("Reading the photo…");
  const { default: Tesseract } = await import("tesseract.js");
  const { data } = await Tesseract.recognize(file, "eng", {
    logger: (m) => {
      if (m.status === "recognizing text") onProgress?.(`Reading the photo… ${Math.round((m.progress || 0) * 100)}%`);
    },
  });
  return data.text || "";
}

const clean = (s) => String(s || "").replace(/\s+/g, " ").trim();
const money = (s) => {
  const m = String(s || "").replace(/[,$]/g, "").match(/\d+(?:\.\d{1,2})?/);
  return m ? m[0] : "";
};

const STATES = "AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC";

function findDate(text, labels) {
  for (const label of labels) {
    const re = new RegExp(label + String.raw`[:\s]*((?:\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})|(?:[A-Za-z]{3,9}\.? \d{1,2},? \d{4}))`, "i");
    const m = text.match(re);
    if (m) return toIso(m[1]);
  }
  return "";
}

export function toIso(raw) {
  if (!raw) return "";
  let m = String(raw).match(/(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})/);
  if (m) {
    const y = m[3].length === 2 ? "20" + m[3] : m[3];
    return `${y}-${String(m[1]).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
  }
  m = String(raw).match(/([A-Za-z]{3,9})\.? (\d{1,2}),? (\d{4})/);
  if (m) {
    const mo = MONTHS[m[1].slice(0, 3).toLowerCase()];
    if (mo !== undefined) return `${m[3]}-${String(mo + 1).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
  }
  return "";
}

// Pull whatever a stub reliably carries out of its text. Returns
// { fields, employeeFields } — the same facts shaped for the pay stub
// form and for an employee record.
export function parseStubText(rawText) {
  const text = String(rawText || "");
  const lines = text.split("\n").map(clean).filter(Boolean);
  const out = {};

  // SSN / masked id: XXX-XX-1234, ***-**-1234, ...5678
  let m = text.match(/(?:XXX|\*{3}|x{3})[-\s]?(?:XX|\*{2}|x{2})[-\s]?(\d{4})/i) || text.match(/SSN[^\d]{0,12}(\d{4})\b/i);
  if (m) out.ssn = m[1];

  // pay frequency
  if (/bi-?weekly/i.test(text)) out.payFrequency = "biweekly";
  else if (/semi-?monthly/i.test(text)) out.payFrequency = "semimonthly";
  else if (/\bweekly\b/i.test(text)) out.payFrequency = "weekly";
  else if (/\bmonthly\b/i.test(text)) out.payFrequency = "monthly";

  // rate + hours (an hourly earnings row: "Regular ... 25.00 80 2,000.00")
  m = text.match(/(?:regular(?: hours| pay)?|hourly)[^\n]*?\$?(\d{1,3}(?:\.\d{2}))\s+(\d{1,3}(?:\.\d{1,2})?)\s/i);
  if (m) { out.payType = "hourly"; out.rate = money(m[1]); out.hoursList = money(m[2]); }
  if (!out.rate) {
    m = text.match(/rate[:\s]+\$?(\d{1,3}(?:\.\d{2})?)/i);
    if (m) { out.payType = "hourly"; out.rate = money(m[1]); }
  }
  if (/salary/i.test(text) && !out.rate) {
    m = text.match(/salary[^\d]{0,10}\$?([\d,]+(?:\.\d{2})?)/i);
    if (m) { out.payType = "salary"; out.annualSalary = money(m[1]); }
  }

  // period + pay date
  m = text.match(/pay period[:\s]*((?:\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})|(?:[A-Za-z]{3,9}\.? \d{1,2},? \d{4}))\s*(?:-|–|to|through)\s*((?:\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})|(?:[A-Za-z]{3,9}\.? \d{1,2},? \d{4}))/i);
  if (m) { out.startDateList = toIso(m[1]); out.endDateList = toIso(m[2]); }
  else {
    const s = findDate(text, [String.raw`period\s+(?:beginning|begin|start(?:ing)?)`, "pay begin"]);
    const e = findDate(text, [String.raw`period\s+end(?:ing)?`, "pay end"]);
    if (s) out.startDateList = s;
    if (e) out.endDateList = e;
  }
  const pd = findDate(text, ["pay (?:day|date)", "check date", "advice date"]);
  if (pd) out.payDateList = pd;
  const hd = findDate(text, ["hire date", "date of hire"]);
  if (hd) out.hireDate = hd;

  // addresses appear anywhere in a line on two-column layouts, so scan the
  // whole text: "City, ST 12345". First hit = company, second = employee,
  // the order every major layout prints them in.
  const cityRe = new RegExp(String.raw`([A-Z][A-Za-z.'-]*(?:\s[A-Z][A-Za-z.'-]*){0,2}),?\s+(${STATES})\.?,?\s+(\d{5})(?:-\d{4})?`, "g");
  const cities = [];
  for (const mm of text.matchAll(cityRe)) cities.push({ city: clean(mm[1]), state: mm[2], zip: mm[3], index: mm.index });
  // street lines the same way: "14 Maple Ave" — first company, second employee
  const streetRe = /\b(\d{1,6}\s+[A-Z][A-Za-z.]*(?:\s[A-Za-z][A-Za-z.]*){0,3}\s(?:Ave(?:nue)?|St(?:reet)?|Blvd|Boulevard|Rd|Road|Dr(?:ive)?|Ln|Lane|Way|Ct|Court|Pl(?:ace)?|Ter(?:race)?|Hwy|Pkwy|Cir(?:cle)?)\b\.?(?:\s(?:Apt|Suite|Ste|Unit|#)\s*[\w-]+)?)/g;
  const streets = [...text.matchAll(streetRe)].map((mm) => ({ street: clean(mm[1]), index: mm.index }));

  if (cities[0]) { out.companyCity = cities[0].city; out.companyState = cities[0].state; out.companyZip = cities[0].zip; }
  if (cities[1]) { out.city = cities[1].city; out.state = cities[1].state; out.zip = cities[1].zip; }
  if (streets[0]) out.companyAddress = streets[0].street;
  if (streets[1]) out.address = streets[1].street;

  // name: an explicit label; else the account line "Kim Lee (...****1234)";
  // else a clean proper-name line just above the employee street
  const okName = (v) => v && !/gross|earning|tax|net|statement|hours|salary|deduction|withheld|company|period|description|employee|address|status|marital/i.test(v);
  m = text.match(/(?:employee(?: name)?|pay(?:able)? to|paid to)[:\s]+([A-Z][A-Za-z'.-]+(?: [A-Z][A-Za-z'.-]+){1,2})/i);
  if (m && okName(m[1])) out.name = clean(m[1]);
  if (!out.name) {
    m = text.match(/^\s*([A-Z][A-Za-z.'-]+(?: [A-Z][A-Za-z.'-]+){1,2})\s*\(\.\.\./m);
    if (m && okName(m[1])) out.name = clean(m[1]);
  }
  if (!out.name && streets[1]) {
    const streetLine = lines.findIndex((ln) => ln.includes(streets[1].street));
    for (let i = streetLine - 1; i >= Math.max(0, streetLine - 3); i--) {
      const cand = (lines[i] || "").trim();
      if (/^[A-Z][A-Za-z.'-]+ [A-Z][A-Za-z.'-]+(?: [A-Z][A-Za-z.'-]+)?$/.test(cand) && okName(cand)) { out.name = cand; break; }
    }
  }

  // company: a clean line within 4 above the company street; else the words
  // in front of the person's name on a shared header line
  if (streets[0]) {
    const streetLine = lines.findIndex((ln) => ln.includes(streets[0].street));
    for (let i = streetLine - 1; i >= Math.max(0, streetLine - 4); i--) {
      const cand = (lines[i] || "").trim();
      if (/^[A-Z][\w.'&-]*(?: [\w.'&-]+){0,4}$/.test(cand) && !/\d/.test(cand) && !/statement|earnings|period|pay|employee|company|date|check/i.test(cand) && cand !== out.name) { out.company = cand; break; }
    }
  }
  if (!out.company && out.name) {
    const esc = out.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    m = text.match(new RegExp(String.raw`^\s*([A-Z][\w.'&-]*(?: [\w.'&-]+){0,4}?) ${esc}\s*$`, "m"));
    if (m && !/statement|earnings|employee|company/i.test(m[1])) out.company = clean(m[1]);
  }
  if (out.name && out.company === out.name) delete out.company;

  const fields = Object.fromEntries(Object.entries(out).filter(([, v]) => v !== "" && v !== undefined));
  const employeeFields = {};
  const map = { name: "name", ssn: "ssnLast4", address: "address", city: "city", state: "state", zip: "zip",
    payType: "payType", rate: "rate", annualSalary: "annualSalary", payFrequency: "payFrequency", hireDate: "hireDate" };
  for (const [k, ek] of Object.entries(map)) if (fields[k] !== undefined) employeeFields[ek] = fields[k];
  if (employeeFields.payFrequency && !["weekly", "biweekly"].includes(employeeFields.payFrequency)) delete employeeFields.payFrequency;
  return { fields, employeeFields };
}
